import React, { useEffect, useState } from 'react';
import {
  View,
  Text,
  FlatList,
  TouchableOpacity,
  StyleSheet,
  TextInput,
  ActivityIndicator,
  SafeAreaView,
  StatusBar,
  Modal,
  Platform,
  Dimensions,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { firestore } from '../../firebaseConfig';
import { collection, query, where, getDocs, updateDoc, doc, serverTimestamp } from 'firebase/firestore';
import VendorCard from './VendorCard';
import VendorProfileScreen from './VendorProfileScreen';
import { StackNavigationProp } from '@react-navigation/stack';

type AllVendorsScreenProps = {
  route: any;
  navigation: StackNavigationProp<any>;
};

type Vendor = {
  vendorName: string;
  location: string;
  profilePicture?: string;
  userId: string;
  category: string;
  // Removed averageRating and review logic.
  createdAt?: number;
  promoted?: boolean;
  promotionExpiration?: number | null;
  // New field for favorites count:
  favoriteCount?: number;
};

export default function AllVendorsScreen({ route, navigation }: AllVendorsScreenProps) {
  const { category, location } = route.params;
  const [vendors, setVendors] = useState<Vendor[]>([]);
  const [filteredVendors, setFilteredVendors] = useState<Vendor[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [selectedVendor, setSelectedVendor] = useState<Vendor | null>(null);
  const [modalVisible, setModalVisible] = useState(false);
  const [sortOption, setSortOption] = useState<'popular' | 'newest' | 'adventurous'>('popular');

  const SCREEN_WIDTH = Dimensions.get('window').width;
  const isMobileWeb = Platform.OS === 'web' && SCREEN_WIDTH <= 600;

  useEffect(() => {
    const fetchVendors = async () => {
      setLoading(true);
      try {
        const vendorCollection = collection(firestore, 'vendors');
        const vendorQuery = query(
          vendorCollection,
          where('location', '==', location),
          where('category', '==', category)
        );
        const vendorQuerySnapshot = await getDocs(vendorQuery);

        const fetchedVendors: Vendor[] = [];

        // For each vendor document, we now simply push the vendor data.
        vendorQuerySnapshot.docs.forEach((vendorDoc) => {
          const vendor = vendorDoc.data() as Vendor;
          // Determine if vendor is promoted
          const isPromoted = !!(vendor.promoted && vendor.promotionExpiration && vendor.promotionExpiration > Date.now());
          fetchedVendors.push({
            ...vendor,
            promoted: isPromoted,
            createdAt: vendor.createdAt || Date.now(),
            userId: vendorDoc.id, // Include the vendor document ID here
          });
        });
        

        // --- Sorting Logic ---
        // First, always ensure promoted vendors are at the top.
        // Then, for "popular" sort, sort by favoriteCount (defaulting to 0 if undefined).
        // For "newest", sort by createdAt.
        // For "adventurous", shuffle the vendors.
        let sortedList: Vendor[] = [];
if (sortOption === 'popular') {
  sortedList = fetchedVendors.sort((a, b) => {
    // Compare promoted first.
    const promotedDiff = (b.promoted ? 1 : 0) - (a.promoted ? 1 : 0);
    if (promotedDiff !== 0) return promotedDiff;
    // Then sort by favoriteCount descending.
    return (b.favoriteCount || 0) - (a.favoriteCount || 0);
  });
} else if (sortOption === 'newest') {
  // For newest, ignore promotion and sort only by createdAt descending.
  sortedList = fetchedVendors.sort((a, b) => {
    return (b.createdAt || 0) - (a.createdAt || 0);
  });
} else if (sortOption === 'adventurous') {
  sortedList = fetchedVendors.sort((a, b) => {
    const promotedDiff = (b.promoted ? 1 : 0) - (a.promoted ? 1 : 0);
    if (promotedDiff !== 0) return promotedDiff;
    return Math.random() - 0.5;
  });
}


        setVendors(sortedList);
        setFilteredVendors(sortedList);
        setLoading(false);
      } catch (error) {
        setErrorMessage('Error fetching vendors');
        setLoading(false);
      }
    };

    fetchVendors();
  }, [location, category, sortOption]);

  // Migrate vendors: ensure that each vendor has a createdAt timestamp.
  useEffect(() => {
    const migrateVendors = async () => {
      const vendorCollection = collection(firestore, 'vendors');
      const vendorQuerySnapshot = await getDocs(vendorCollection);

      for (const vendorDoc of vendorQuerySnapshot.docs) {
        const vendorData = vendorDoc.data() as Vendor;
        if (!vendorData.createdAt) {
          const vendorRef = doc(firestore, 'vendors', vendorDoc.id);
          await updateDoc(vendorRef, {
            createdAt: serverTimestamp(),
          });
        }
      }
    };

    migrateVendors();
  }, []);

  const handleSearch = (query: string) => {
    setSearchQuery(query);
    if (query.trim() === '') {
      setFilteredVendors(vendors);
    } else {
      const filtered = vendors.filter((vendor) =>
        vendor.vendorName.toLowerCase().includes(query.toLowerCase())
      );
      setFilteredVendors(filtered);
    }
  };

  // Since we already sorted vendors in the fetch effect, simply return filtered vendors.
  const sortedVendors = () => {
    return filteredVendors;
  };

  return (
    <SafeAreaView style={styles.container}>
      <StatusBar barStyle="dark-content" />

      {/* Header */}
      <View style={styles.header}>
        <TouchableOpacity onPress={() => navigation.navigate('Home - Aweyy')} style={styles.backButton}>
          <Ionicons name="arrow-back" size={28} color="#000000" />
        </TouchableOpacity>
        <Text style={styles.title}>All {category} in {location}</Text>
      </View>

      {/* Search Bar */}
      <View style={styles.searchContainer}>
        <Ionicons name="search" size={20} color="#333" />
        <TextInput
          style={styles.searchInput}
          placeholder="Search vendors..."
          value={searchQuery}
          onChangeText={handleSearch}
        />
      </View>

      {/* Sort Options */}
      <View style={styles.sortContainer}>
        <TouchableOpacity
          onPress={() => setSortOption('popular')}
          style={[styles.sortButton, sortOption === 'popular' && styles.activeSortButton]}
        >
          <Text style={styles.sortText}>Most Popular</Text>
        </TouchableOpacity>
        <TouchableOpacity
          onPress={() => setSortOption('newest')}
          style={[styles.sortButton, sortOption === 'newest' && styles.activeSortButton]}
        >
          <Text style={styles.sortText}>Newest</Text>
        </TouchableOpacity>
        <TouchableOpacity
          onPress={() => setSortOption('adventurous')}
          style={[styles.sortButton, sortOption === 'adventurous' && styles.activeSortButton]}
        >
          <Text style={styles.sortText}>Adventurous</Text>
        </TouchableOpacity>
      </View>

      {/* Vendor List */}
      {loading ? (
        <ActivityIndicator size="large" color="#b6cc9b" />
      ) : errorMessage ? (
        <Text style={styles.errorText}>{errorMessage}</Text>
      ) : (
        <FlatList
          data={sortedVendors()}
          key={isMobileWeb ? 'mobile' : 'desktop'}
          numColumns={isMobileWeb ? 1 : 2}
          keyExtractor={(item) => item.userId}
          columnWrapperStyle={!isMobileWeb && styles.row}
          renderItem={({ item }) => (
            <View style={isMobileWeb ? styles.centeredVendorContainer : styles.vendorContainer}>
              <TouchableOpacity
                style={[styles.vendorCardWrapper, { width: isMobileWeb ? '90%' : '95%' }]}
                onPress={() => {
                  setSelectedVendor(item);
                  setModalVisible(true);
                }}
              >
                <VendorCard vendor={item} />
                {item.promoted && <Text style={styles.adLabel}>Ad</Text>}
              </TouchableOpacity>
            </View>
          )}
          contentContainerStyle={styles.vendorList}
        />
      )}

      {/* Vendor Profile Modal */}
      {selectedVendor && (
        <Modal visible={modalVisible} animationType="slide" onRequestClose={() => setModalVisible(false)}>
          <VendorProfileScreen
            vendor={selectedVendor}
            isExplorer={true}
            onClose={() => setModalVisible(false)}
          />
        </Modal>
      )}
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#FFF',
    paddingTop: StatusBar.currentHeight || 10,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 20,
    paddingHorizontal: 20,
  },
  backButton: {
    padding: 10,
  },
  title: {
    fontSize: 24,
    fontWeight: 'bold',
    color: '#4A4A4A',
    flex: 1,
    textAlign: 'center',
  },
  searchContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#fff',
    paddingHorizontal: 15,
    paddingVertical: 10,
    borderRadius: 15,
    borderWidth: 1,
    borderColor: '#b6cc9b',
    marginBottom: 20,
    marginHorizontal: 20,
  },
  searchInput: {
    marginLeft: 10,
    flex: 1,
    fontSize: 16,
    color: '#a4a4a4',
  },
  vendorList: {
    paddingHorizontal: 15,
    paddingBottom: 20,
  },
  row: {
    justifyContent: 'space-between',
  },
  vendorContainer: {
    flex: 1,
    marginBottom: 15,
    alignItems: 'center',
    maxWidth: '48%',
  },
  centeredVendorContainer: {
    flex: 1,
    marginBottom: 15,
    alignItems: 'center',
    justifyContent: 'center',
  },
  vendorCardWrapper: {
    width: '100%',
    alignItems: 'center',
  },
  adLabel: {
    position: 'absolute',
    top: 5,
    left: 5,
    backgroundColor: '#fff',
    color: '#a4a4a4',
    paddingHorizontal: 5,
    paddingVertical: 2,
    borderRadius: 5,
    fontSize: 10,
    fontWeight: 'bold',
  },
  sortContainer: {
    flexDirection: 'row',
    justifyContent: 'space-around',
    marginHorizontal: 20,
    marginBottom: 20,
  },
  sortButton: {
    paddingVertical: 8,
    paddingHorizontal: 15,
    backgroundColor: '#FFFFFF',
    borderRadius: 15,
    borderWidth: 1,
    borderColor: '#b6cc9b',
  },
  activeSortButton: {
    backgroundColor: '#b6cc9b',
  },
  sortText: {
    fontSize: 16,
    fontWeight: '600',
    color: '#4A4A4A',
  },
  errorText: {
    color: 'red',
    textAlign: 'center',
    marginBottom: 10,
  },
});
